import { createContext, useContext, useState, useEffect } from 'react'
import axios from 'axios'
import { Prompt } from 'react-router-dom'

import { errorHandlerService } from '../../common/services/error-handler.service'
import NotificationManager from '../../common/components/Notifications/NotificationManager'
import { Report } from '../report.model'
import { unsavedChangesService } from '../editor/unsaved-changes.service'
import { blockEditService } from '../editor/block-edit.service'
import { PAGE_CONTROL_BAR_KEY } from '../constants'

class PageEditor {
  constructor ({
    pageId, page, setPage, isSaving, setIsSaving, currentEdit, setCurrentEdit, pagePropertiesToDelete = {}
  }) {
    this.pageId = pageId
    this.page = page
    this.setPage = setPage
    this.isSaving = isSaving
    this.setIsSaving = setIsSaving
    this.currentEdit = currentEdit
    this.setCurrentEditState = setCurrentEdit
    this.pagePropertiesToDelete = pagePropertiesToDelete
  }

  /**
   * Updates the whole currentEdit object - warning if you try to switch to a different block with unsaved changes
   *
   * @param {object} currentEdit
   * @param {boolean} force
   */
  setCurrentEdit (currentEdit, force = false) {
    const { block: previousEditBlock = {} } = this.currentEdit || {}
    const { block: newEditBlock = {} } = currentEdit || {}

    // Updating current edit block with changes is ok - switching to a different block is not
    if (!force && unsavedChangesService.hasUnsavedChanges && newEditBlock.key !== previousEditBlock.key) {
      this.warnUnsavedChanges()
    } else {
      this.currentEdit = currentEdit // handle multiple currentEdit updates in a row (before this object gets recreated)
      this.setCurrentEditState(currentEdit)
    }
  }

  /**
   * Update just a portion of the currentEdit object (and then set current edit with all of it's associated logic)
   *
   * @param {object} currentEditUpdates
   */
  updateCurrentEdit (currentEditUpdates) {
    this.setCurrentEdit({ ...(this.currentEdit || {}), ...currentEditUpdates })
  }

  registerUnsavedChanges (revertFunction = () => { this.setCurrentEdit(null, true) }) {
    unsavedChangesService.registerUnsavedChanges(revertFunction)
  }

  // Saves block updates including deletion
  async savePage () {
    this.setIsSaving(true)
    const currentEdit = this.currentEdit || {}

    try {
      // Allow block editors to validate logic before save and stop if needed
      if (currentEdit.validate) {
        const shouldContinue = await currentEdit.validate()
        if (shouldContinue === false) {
          this.setIsSaving(false)
          return
        }
      }

      // Call on before save to make any modifications before saving
      if (currentEdit.onBeforeSave) {
        currentEdit.onBeforeSave(this)
      }

      if (currentEdit.pageControlBar || currentEdit.shouldRemovePageControlBar) {
        this.page.layout.pageControlBar = currentEdit.pageControlBar
      }

      if (currentEdit.block) {
        if (currentEdit.block.key === PAGE_CONTROL_BAR_KEY) {
          this.page.layout.pageControlBar = currentEdit.block.pageControlBar
        } else if (!currentEdit.block.isPageProperty) {
          this.replaceBlockByKey(currentEdit.block)
        }
      }

      const requestBody = { layout: this.page.layout }
      if (currentEdit.title) {
        requestBody.title = currentEdit.title
      }
      if (currentEdit.description !== undefined) {
        requestBody.description = currentEdit.description
      }

      // Delete any page properties if they were flagged to be deleted
      for (let key of Object.keys(this.pagePropertiesToDelete)) {
        requestBody[key] = ''
      }
      this.pagePropertiesToDelete = {}
      // Filter out any page property blocks, which are "pseudo blocks"
      // that should never get saved in the actual blocks array
      requestBody.layout.blocks = requestBody.layout.blocks.filter((block) => !block.isPageProperty)
      let { data } = await axios.post(`api/report/${this.pageId}`, requestBody)

      // Allow block editors to run logic after save and manipulate the layout data
      if (currentEdit.onSave) {
        const updatedData = await currentEdit.onSave(data)
        if (updatedData) {
          data = updatedData
        }
      }
      this.setPage(new Report(data))
    } catch (error) {
      errorHandlerService.handleServerError(error, 'Sorry, we are having some problems saving this page. Try again?')
      return
    } finally {
      this.setIsSaving(false)
    }

    unsavedChangesService.clearUnsavedChanges()
    this.setCurrentEdit(null, true)
    NotificationManager.create({ type: 'success', timeOut: 5000, message: 'Page saved.' })
  }

  cancelPageEdit () {
    if (unsavedChangesService.hasUnsavedChanges) {
      unsavedChangesService.revert()
      unsavedChangesService.clearUnsavedChanges()
    }

    this.setCurrentEdit(null)
  }

  async createNewBlock (blockId, atIndex) {
    const { newBlock, shouldEditOnDrop } = blockEditService.buildNewBlock(blockId)

    this.addBlock(newBlock, atIndex)
    await this.savePage()
    if (shouldEditOnDrop) {
      this.setCurrentEdit({ block: this.page.layout.blocks.find(({ key }) => key === newBlock.key) })
    }
  }

  addBlock (block, atIndex) {
    if (unsavedChangesService.hasUnsavedChanges) {
      this.warnUnsavedChanges()
      return
    }

    this.page.layout.blocks.splice(atIndex, 0, block)
  }

  reorderBlocks (startIndex, endIndex) {
    if (unsavedChangesService.hasUnsavedChanges) {
      this.warnUnsavedChanges()
      return
    }

    const blocks = this.page.layout.blocks
    const [block] = blocks.splice(startIndex, 1)
    blocks.splice(endIndex, 0, block)
  }

  replaceBlockByKey (block) {
    const blocks = this.page.layout.blocks
    const index = blocks.findIndex(({ key }) => key === block.key)
    blocks.splice(index, 1, block)
  }

  deleteBlock (deleteKey) {
    // Deletes the block which will update state prior to db update
    if (unsavedChangesService.hasUnsavedChanges) {
      this.warnUnsavedChanges()
      return
    }
    this.page.layout.blocks = this.page.layout.blocks.filter(({ key }) => key !== deleteKey)
  }

  deletePageProperty (propertyName) {
    this.pagePropertiesToDelete[propertyName] = true
  }

  warnUnsavedChanges () {
    const message = 'You have unsaved changes. Please save or cancel before continuing.'
    NotificationManager.create({ type: 'warning', timeOut: 5000, message })
  }
}

const PageEditorContext = createContext()

/**
 * Note: Page Editor Context is specific to the editor and will not return a value in "read" mode
 * We should move away from using this in components that are not editor specific components
 */
export const usePageEditorContext = function () {
  return useContext(PageEditorContext)
}

export const PageEditorContextProvider = function ({ pageId, children }) {
  const [page, setPage] = useState()
  const [isSaving, setIsSaving] = useState(false)
  const [currentEdit, setCurrentEdit] = useState(null)

  useEffect(() => {
    const beforeUnloadHandler = unsavedChangesService.beforeUnload.bind(unsavedChangesService)
    window.addEventListener('beforeunload', beforeUnloadHandler)
    return () => {
      window.removeEventListener('beforeunload', beforeUnloadHandler)
      unsavedChangesService.clearUnsavedChanges()
    }
  }, [])

  if (page && page.layout && page.layout.blocks) {
    page.layout.blocks = page.layout.blocks.filter((block) => block.key !== 'title')
  }

  const pageEditorContext = new PageEditor({
    pageId,
    page,
    setPage,
    isSaving,
    setIsSaving,
    currentEdit,
    setCurrentEdit
  })

  return (
    <PageEditorContext.Provider value={pageEditorContext}>
      {children}
      <Prompt
        when={unsavedChangesService.hasUnsavedChanges}
        message="You have unsaved changes. Are you sure you want to leave?"
      />
    </PageEditorContext.Provider>
  )
}
